import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dirigenteService from '../services/dirigenteService';
import type { Dirigente } from '../types/dirigente.types';
import {
  SEXO_OPTIONS,
  ESTADO_CIVIL_OPTIONS,
  CARGO_OPTIONS,
  UF_OPTIONS
} from '../types/dirigente.types';
import { lookupCep } from '../services/cepService';
import { isValidEmail, toUpperText } from '../utils/formatters';
import institutionService from '../services/institutionService';
import DocumentosPessoaisUpload from '../components/DocumentosPessoaisUpload';
import { UI_THEME } from '../ui/theme';
import { notifyError, notifyInfo, notifySuccess } from '../utils/notify';

const CadastroDirigentePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const instituicaoId = searchParams.get('instituicaoId');
  const dirigenteId = searchParams.get('id');
  const isEditMode = !!dirigenteId;

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [vincularLoading, setVincularLoading] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dados'|'documentos'>('dados');
  const [foundDirigente, setFoundDirigente] = useState<any | null>(null);
  const [institutionNames, setInstitutionNames] = useState<Record<string, string>>({});
  // UX: modo minimal exibe apenas CPF + Cargo inicialmente para evitar tela cheia de campos
  const [minimalMode, setMinimalMode] = useState<boolean>(!isEditMode);
  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const headerClass = isSaas
    ? 'rounded-2xl border border-teal-200/70 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-5 shadow-sm dark:border-teal-500/20 dark:from-slate-950 dark:via-slate-900 dark:to-teal-950/40'
    : 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950';
  const panelClass = isSaas
    ? 'rounded-2xl border border-teal-200/70 bg-white shadow-sm dark:border-teal-500/20 dark:bg-slate-950'
    : 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950';
  const mutedTextClass = 'text-sm text-slate-600 dark:text-slate-400';
  const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200';
  const fieldBaseClass = 'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:disabled:bg-slate-800';
  const fieldClass = (field: string, extra = '') =>
    `${fieldBaseClass} ${errors[field] ? 'border-red-500 bg-red-50 dark:border-red-500/70 dark:bg-red-950/20' : ''} ${extra}`;
  const errorClass = 'mt-1 text-xs text-red-600 dark:text-red-300';
  const primaryButtonClass = isSaas
    ? 'inline-flex items-center justify-center rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60'
    : 'inline-flex items-center justify-center rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  const [formData, setFormData] = useState<Dirigente>({
    instituicaoId: instituicaoId || '',
    nomeCompleto: '',
    cpf: '',
    rg: '',
    orgaoExpedidor: '',
    ufOrgaoExpedidor: '',
    dataExpedicao: '',
    dataNascimento: '',
    sexo: '',
    nacionalidade: 'Brasileira',
    estadoCivil: '',
    cargo: '',
    dataInicioCargo: '',
    statusCargo: 'Ativo',
    telefone: '',
    email: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: ''
  });

  useEffect(() => {
    if (isEditMode && dirigenteId) {
      loadDirigente();
    }
  }, [dirigenteId]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const loadDirigente = async () => {
    try {
      setInitialLoading(true);
      const data = await dirigenteService.obter(dirigenteId!);
      setFormData(data);
    } catch (error) {
      console.error('Erro ao carregar dirigente:', error);
      notifyError('Erro ao carregar dados do dirigente');
    } finally {
      setInitialLoading(false);
    }
  };

  const getNomeCampo = (campo: string): string => {
    const nomes: Record<string, string> = {
      nomeCompleto: 'Nome Completo',
      cpf: 'CPF',
      rg: 'RG',
      orgaoExpedidor: 'Órgão Expedidor',
      ufOrgaoExpedidor: 'UF do Órgão',
      dataExpedicao: 'Data de Expedição',
      dataNascimento: 'Data de Nascimento',
      sexo: 'Sexo',
      estadoCivil: 'Estado Civil',
      cargo: 'Cargo',
      dataInicioCargo: 'Data de Início do Cargo',
      email: 'E-mail',
      telefone: 'Telefone',
      cep: 'CEP',
      logradouro: 'Logradouro',
      numero: 'Número',
      complemento: 'Complemento',
      bairro: 'Bairro',
      cidade: 'Cidade',
      uf: 'UF'
    };
    return nomes[campo] || campo;
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.nomeCompleto || formData.nomeCompleto.trim().length < 3) {
      errors.nomeCompleto = 'Nome completo deve ter no mínimo 3 caracteres';
    }

    if (!formData.cpf || formData.cpf.replace(/\D/g, '').length !== 11) {
      errors.cpf = 'CPF inválido (deve ter 11 dígitos)';
    }

    if (!formData.orgaoExpedidor || formData.orgaoExpedidor.trim().length < 2) {
      errors.orgaoExpedidor = 'Órgão expedidor inválido';
    }

    if (!formData.ufOrgaoExpedidor) {
      errors.ufOrgaoExpedidor = 'Selecione a UF';
    }

    if (!formData.dataExpedicao) {
      errors.dataExpedicao = 'Data de expedição obrigatória';
    } else if (new Date(formData.dataExpedicao) > new Date()) {
      errors.dataExpedicao = 'Data não pode ser futura';
    }

    if (!formData.dataNascimento) {
      errors.dataNascimento = 'Data de nascimento obrigatória';
    } else {
      const idade = new Date().getFullYear() - new Date(formData.dataNascimento).getFullYear();
      if (idade < 18) {
        errors.dataNascimento = 'Dirigente deve ter no mínimo 18 anos';
      }
      if (idade > 120) {
        errors.dataNascimento = 'Data de nascimento inválida';
      }
    }

    if (!formData.sexo) {
      errors.sexo = 'Selecione o sexo';
    }

    if (!formData.estadoCivil) {
      errors.estadoCivil = 'Selecione o estado civil';
    }

    if (!formData.cargo) {
      errors.cargo = 'Selecione o cargo';
    }

    if (!formData.dataInicioCargo) {
      errors.dataInicioCargo = 'Data de início obrigatória';
    }

    if (!formData.email || !isValidEmail(formData.email)) {
      errors.email = 'Email inválido';
    }

    if (!formData.telefone || formData.telefone.replace(/\D/g, '').length < 10) {
      errors.telefone = 'Telefone inválido (mínimo 10 dígitos)';
    }

    if (!formData.cep || formData.cep.replace(/\D/g, '').length !== 8) {
      errors.cep = 'CEP inválido (deve ter 8 dígitos)';
    }

    if (!formData.logradouro || formData.logradouro.trim().length < 3) {
      errors.logradouro = 'Logradouro inválido';
    }

    if (!formData.numero || formData.numero.trim().length === 0) {
      errors.numero = 'Número obrigatório';
    } else if (formData.numero.trim().length > 10) {
      errors.numero = 'Número deve ter no máximo 10 caracteres. Use o campo Complemento para informações adicionais.';
    }

    if (!formData.bairro || formData.bairro.trim().length < 2) {
      errors.bairro = 'Bairro inválido';
    }

    if (!formData.cidade || formData.cidade.trim().length < 2) {
      errors.cidade = 'Cidade inválida';
    }

    if (!formData.uf) {
      errors.uf = 'Selecione a UF';
    }

    return errors;
  };

  const handleBuscarCep = async () => {
    const cepLimpo = formData.cep.replace(/\D/g, '');

    if (cepLimpo.length !== 8) {
      notifyError('CEP inválido. Digite 8 dígitos.');
      return;
    }

    try {
      setLoadingCep(true);
      const endereco = await lookupCep(cepLimpo);

      setFormData({
        ...formData,
        logradouro: endereco.logradouro || '',
        bairro: endereco.bairro || '',
        cidade: endereco.localidade || '',
        uf: endereco.uf || ''
      });

      const newErrors = { ...errors };
      delete newErrors.cep;
      delete newErrors.logradouro;
      delete newErrors.bairro;
      delete newErrors.cidade;
      delete newErrors.uf;
      setErrors(newErrors);

      notifySuccess('Endereço preenchido com sucesso!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      notifyError('CEP não encontrado. Preencha manualmente.');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCpfLookup = async (rawCpf?: string) => {
    const cpfToLookup = (rawCpf || formData.cpf || '').replace(/\D/g, '');
    if (!cpfToLookup) return;
    try {
      const found = await dirigenteService.buscarPorCpf(cpfToLookup);
      if (!found) {
        setFoundDirigente(null);
        notifyInfo('CPF não encontrado. Você pode continuar e cadastrar a nova pessoa.');
        return;
      }
      setFoundDirigente(found);
      // Only keep essential info populated (do not reveal entire form yet)
      setFormData(prev => ({
        ...prev,
        cpf: (found.cpf || '').replace(/\D/g, ''),
        nomeCompleto: found.nomeCompleto || prev.nomeCompleto,
        email: found.email || prev.email,
        telefone: found.telefone || prev.telefone,
      }));
      // fetch institution names for vinculos (if any)
      if (found.vinculos && found.vinculos.length > 0) {
        const ids = Array.from(new Set(found.vinculos.map((v: any) => v.instituicaoId).filter(Boolean)));
        const toFetch = ids.filter((id: string) => !institutionNames[id]);
        if (toFetch.length > 0) {
          try {
            const results = await Promise.all(toFetch.map((id: string) => institutionService.getMinimos(id)));
            const map: Record<string, string> = {};
            results.forEach((r: any, idx: number) => {
              const id = toFetch[idx];
              if (r && (r.razaoSocial || r.nomeFantasia)) {
                map[id] = r.razaoSocial || r.nomeFantasia || id;
              } else {
                map[id] = id;
              }
            });
            setInstitutionNames(prev => ({ ...prev, ...map }));
          } catch (e) {
            console.warn('Erro ao buscar nomes de instituições', e);
          }
        }
      }
    } catch (err: any) {
      console.error('Erro ao buscar CPF:', err);
      notifyError(err?.response?.data?.error || 'Erro ao buscar por CPF');
    }
  };

  // Called from the minimal flow when user chooses to continue to full form
  const handleContinueFromMinimal = () => {
    // Validate CPF and Cargo minimally
    const cpfClean = (formData.cpf || '').replace(/\D/g, '');
    if (cpfClean.length !== 11) {
      notifyError('CPF inválido. Digite 11 dígitos.');
      return;
    }
    if (!formData.cargo) {
      notifyError('Selecione o cargo antes de continuar.');
      return;
    }
    // reveal full form so user can fill remaining fields
    setMinimalMode(false);
  };

  const normalizeText = (value: string) =>
    (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const hasExistingActiveVinculoForSelectedCargo = () => {
    if (!foundDirigente || !formData.cargo) return false;

    const targetInstituicaoId = instituicaoId || formData.instituicaoId;
    if (!targetInstituicaoId) return false;

    return Array.isArray(foundDirigente.vinculos) && foundDirigente.vinculos.some((v: any) =>
      v?.instituicaoId === targetInstituicaoId &&
      v?.cargo === formData.cargo &&
      v?.statusCargo === 'Ativo'
    );
  };

  const handleConfirmVinculoFound = async () => {
    if (!foundDirigente) return;
    if (!formData.cargo) { notifyError('Selecione o cargo para vincular.'); return; }
    const targetInstituicaoId = instituicaoId || formData.instituicaoId;
    if (!targetInstituicaoId) {
      notifyError('Instituição não identificada para criar o vínculo.');
      return;
    }

    if (hasExistingActiveVinculoForSelectedCargo()) {
      setSuccessMessage('O vínculo já existia. Redirecionando para a lista da diretoria...');
      setTimeout(() => navigate(`/dashboard/cadastro-dados-institucionais?id=${targetInstituicaoId}&tab=dirigentes`), 800);
      return;
    }

    try {
      setVincularLoading(true);
      await dirigenteService.criarVinculo(foundDirigente.id!, {
        instituicaoId: targetInstituicaoId,
        cargo: formData.cargo,
        ...(formData.dataInicioCargo ? { dataInicioCargo: formData.dataInicioCargo } : {}),
        statusCargo: formData.statusCargo,
      });
      setSuccessMessage('Vínculo criado com sucesso');
      setTimeout(() => navigate(`/dashboard/cadastro-dados-institucionais?id=${targetInstituicaoId}`), 800);
    } catch (err: any) {
      console.error('Erro ao criar vinculo:', err);
      const errorMessage = err?.response?.data?.error || 'Erro ao criar vinculo';
      const normalizedError = normalizeText(String(errorMessage));

      if (normalizedError.includes('vinculo ativo')) {
        setSuccessMessage('O vínculo já existia. Redirecionando para a lista da diretoria...');
        setTimeout(() => navigate(`/dashboard/cadastro-dados-institucionais?id=${targetInstituicaoId}&tab=dirigentes`), 800);
        return;
      }

      notifyError(errorMessage);
    } finally {
      setVincularLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      const camposComErro = Object.entries(validationErrors).map(([campo, mensagem]) => {
        const nomeCampo = getNomeCampo(campo);
        return `• ${nomeCampo}: ${mensagem}`;
      }).join('\n');

      notifyError(`Por favor, corrija os seguintes erros:\n\n${camposComErro}`);

      setTimeout(() => {
        const firstErrorField = document.querySelector('.border-red-500') as HTMLElement;
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);

      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await dirigenteService.atualizar(dirigenteId!, formData);
        setSuccessMessage('Dirigente atualizado com sucesso!');
      } else {
        await dirigenteService.criar(formData);
        setSuccessMessage('Dirigente cadastrado com sucesso!');
      }
      navigate(`/dashboard/cadastro-dados-institucionais?id=${instituicaoId}`);
    } catch (error: any) {
      console.error('Erro ao salvar dirigente:', error);
      notifyError(error.response?.data?.error || 'Erro ao salvar dirigente');
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 10) {
      return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const formatCEP = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  if (initialLoading && isEditMode) {
    return (
      <div className="w-full">
        <div className={`${panelClass} p-12 text-center`}>
          <p className="text-slate-500 dark:text-slate-400">Carregando dados do dirigente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <header className={headerClass}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>Diretoria</p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
          {isEditMode ? 'Editar dirigente' : 'Cadastrar novo dirigente'}
        </h1>
        <p className={`mt-2 max-w-3xl leading-6 ${mutedTextClass}`}>
          Preencha os dados do dirigente. Campos marcados com * são obrigatórios.
        </p>
      </header>

      {successMessage && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-200">
          {successMessage}
        </div>
      )}

      {/* Tabs: Dados / Documentos */}
      <div className={`${panelClass} p-4`}>
        <nav className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setActiveTab('dados')}
            className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'dados'
                ? isSaas
                  ? 'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-500/50 dark:bg-teal-950/30 dark:text-teal-200'
                  : 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/50 dark:bg-blue-950/30 dark:text-blue-200'
                : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-900'
            }`}>
            Dados
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('documentos')}
            disabled={!isEditMode}
            className={`rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'documentos'
                ? isSaas
                  ? 'border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-500/50 dark:bg-teal-950/30 dark:text-teal-200'
                  : 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/50 dark:bg-blue-950/30 dark:text-blue-200'
                : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-900'
            } ${!isEditMode ? 'cursor-not-allowed opacity-50' : ''}`}>
            Documentos
          </button>
        </nav>
      </div>

      {/* Dados tab */}
      {activeTab === 'dados' && (
        <form onSubmit={handleSubmit} noValidate={!!foundDirigente} className={panelClass}>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
              {foundDirigente ? (
                // Found: Show compact vinculo panel (only cargo/date) to confirm
                <div className="col-span-full">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/40 dark:bg-emerald-950/20">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Dirigente encontrado</div>
                        <div className="text-sm text-emerald-800 dark:text-emerald-200">{foundDirigente.nomeCompleto} - CPF: {formatCPF(foundDirigente.cpf || '')}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-300">E-mail: {foundDirigente.email} | Telefone: {foundDirigente.telefone}</div>
                        {foundDirigente.vinculos && foundDirigente.vinculos.length > 0 && (
                          <div className="mt-3">
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Vínculos existentes</div>
                            <ul className="mt-2 space-y-2">
                              {foundDirigente.vinculos.map((v: any) => (
                                <li key={v.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <div className="font-medium text-slate-900 dark:text-white">{v.cargo} <span className="text-xs text-slate-500 dark:text-slate-400">({v.statusCargo})</span></div>
                                      <div className="text-xs text-slate-500 dark:text-slate-400">Instituição: {institutionNames[v.instituicaoId] || v.instituicaoId || '-'}</div>
                                    </div>
                                    <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                                      <div>Início: {v.dataInicioCargo ? new Date(v.dataInicioCargo).toLocaleDateString() : '—'}</div>
                                      <div>Término: {v.dataTerminoCargo ? new Date(v.dataTerminoCargo).toLocaleDateString() : '—'}</div>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                         <div className="mt-3">
                           <label className={labelClass}>Cargo a vincular *</label>
                           <select
                             value={formData.cargo}
                             onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                             className={fieldBaseClass}
                             required={!foundDirigente}
                           >
                             <option value="">Selecione</option>
                             {CARGO_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                           </select>
                         </div>
                         <div className="mt-2">
                          <label className={labelClass}>Data Início</label>
                          <input type="date" value={formData.dataInicioCargo} onChange={(e) => setFormData({ ...formData, dataInicioCargo: e.target.value })} className={fieldBaseClass} />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={handleConfirmVinculoFound} disabled={loading} className={primaryButtonClass}>
                          {vincularLoading ? (
                            <>
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Vinculando...
                            </>
                          ) : (
                            'Vincular'
                          )}
                        </button>
                        <button type="button" onClick={() => { setFoundDirigente(null); }} className={secondaryButtonClass}>Cancelar</button>
                       </div>
                     </div>
                   </div>
                 </div>
              ) : (
                <>
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className={labelClass}>CPF *</label>
                    <div className="flex gap-2">
                      <input
                          type="text"
                          required={!foundDirigente}
                          value={formatCPF(formData.cpf)}
                          onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                          className={`${fieldBaseClass} flex-1 min-w-0`}
                          maxLength={14}
                          disabled={isEditMode}
                          title={isEditMode ? 'O CPF identifica a pessoa e não pode ser alterado na edição' : undefined}
                        />
                      {!isEditMode && (
                        <button
                          type="button"
                          title="Buscar por CPF"
                          onClick={() => handleCpfLookup()}
                          className={`${primaryButtonClass} whitespace-nowrap`}
                        >
                          Buscar
                        </button>
                      )}
                    </div>
                    {isEditMode && (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Não pode ser alterado. Para vincular outra pessoa, use “+ Novo Dirigente”.
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className={labelClass}>Nome Completo *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      value={formData.nomeCompleto}
                      onChange={(e) => setFormData({ ...formData, nomeCompleto: toUpperText(e.target.value) })}
                      className={fieldBaseClass}
                    />
                  </div>
                </>
              )}

              {/* Se encontrarmos um dirigente, ocultamos todo o restante do formulário
                 (campos a partir de RG até UF) para deixar apenas o painel de vínculo.
                 Caso contrário, renderizamos o bloco completo de campos abaixo. */}
              {!foundDirigente && (
                <>
                  {/* Linha de documento: RG / Órgão / UF Órgão / Data Expedição
                      em sub-grid de 12 colunas para deixar Órgão e UF Órgão estreitos */}
                  <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-4 gap-y-4">
                  {/* RG */}
                  <div className="lg:col-span-4">
                    <label className={labelClass}>RG</label>
                    <input
                      type="text"
                      value={formData.rg}
                      onChange={(e) => {
                        setFormData({ ...formData, rg: toUpperText(e.target.value) });
                        clearError('rg');
                      }}
                      className={fieldClass('rg')}
                    />
                    {errors.rg && (
                      <p className={errorClass}>{errors.rg}</p>
                    )}
                  </div>

                  {/* Órgão Expedidor */}
                  <div className="lg:col-span-2">
                    <label className={labelClass}>Órgão Expedidor *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      value={formData.orgaoExpedidor}
                      onChange={(e) => {
                        setFormData({ ...formData, orgaoExpedidor: toUpperText(e.target.value) });
                        clearError('orgaoExpedidor');
                      }}
                      className={fieldClass('orgaoExpedidor')}
                      placeholder="Ex: SSP"
                    />
                    {errors.orgaoExpedidor && (
                      <p className={errorClass}>{errors.orgaoExpedidor}</p>
                    )}
                  </div>

                  {/* UF Órgão */}
                  <div className="lg:col-span-2">
                    <label className={labelClass}>UF Órgão *</label>
                    <select
                      required={!foundDirigente}
                      value={formData.ufOrgaoExpedidor}
                      onChange={(e) => {
                        setFormData({ ...formData, ufOrgaoExpedidor: e.target.value });
                        clearError('ufOrgaoExpedidor');
                      }}
                      className={fieldClass('ufOrgaoExpedidor')}
                    >
                      <option value="">Selecione</option>
                      {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                    {errors.ufOrgaoExpedidor && (
                      <p className={errorClass}>{errors.ufOrgaoExpedidor}</p>
                    )}
                  </div>

                  {/* Data Expedição */}
                  <div className="lg:col-span-4">
                    <label className={labelClass}>Data Expedição *</label>
                    <input
                      type="date"
                      required={!foundDirigente}
                      value={formData.dataExpedicao}
                      onChange={(e) => {
                        setFormData({ ...formData, dataExpedicao: e.target.value });
                        clearError('dataExpedicao');
                      }}
                      className={fieldClass('dataExpedicao')}
                    />
                    {errors.dataExpedicao && (
                      <p className={errorClass}>{errors.dataExpedicao}</p>
                    )}
                  </div>
                  </div>

                  {/* Data de Nascimento */}
                  <div>
                    <label className={labelClass}>Data de Nascimento *</label>
                    <input
                      type="date"
                      required={!foundDirigente}
                      value={formData.dataNascimento}
                      onChange={(e) => {
                        setFormData({ ...formData, dataNascimento: e.target.value });
                        clearError('dataNascimento');
                      }}
                      className={fieldClass('dataNascimento')}
                    />
                    {errors.dataNascimento && (
                      <p className={errorClass}>{errors.dataNascimento}</p>
                    )}
                  </div>

                  {/* Sexo */}
                  <div>
                    <label className={labelClass}>Sexo *</label>
                    <select
                      required={!foundDirigente}
                      value={formData.sexo}
                      onChange={(e) => {
                        setFormData({ ...formData, sexo: e.target.value });
                        clearError('sexo');
                      }}
                      className={fieldClass('sexo')}
                    >
                      <option value="">Selecione</option>
                      {SEXO_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.sexo && (
                      <p className={errorClass}>{errors.sexo}</p>
                    )}
                  </div>

                  {/* Estado Civil */}
                  <div>
                    <label className={labelClass}>Estado Civil *</label>
                    <select
                      required={!foundDirigente}
                      value={formData.estadoCivil}
                      onChange={(e) => {
                        setFormData({ ...formData, estadoCivil: e.target.value });
                        clearError('estadoCivil');
                      }}
                      className={fieldClass('estadoCivil')}
                    >
                      <option value="">Selecione</option>
                      {ESTADO_CIVIL_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                    {errors.estadoCivil && (
                      <p className={errorClass}>{errors.estadoCivil}</p>
                    )}
                  </div>

                  {/* Cargo */}
                  <div>
                    <label className={labelClass}>Cargo *</label>
                    <select
                      required={!foundDirigente}
                      value={formData.cargo}
                      onChange={(e) => {
                        setFormData({ ...formData, cargo: e.target.value });
                        clearError('cargo');
                      }}
                      className={fieldClass('cargo')}
                    >
                      <option value="">Selecione</option>
                      {CARGO_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.cargo && (
                      <p className={errorClass}>{errors.cargo}</p>
                    )}
                  </div>

                  {/* Data Início */}
                  <div>
                    <label className={labelClass}>Data Início *</label>
                    <input
                      type="date"
                      required={!foundDirigente}
                      value={formData.dataInicioCargo}
                      onChange={(e) => {
                        setFormData({ ...formData, dataInicioCargo: e.target.value });
                        clearError('dataInicioCargo');
                      }}
                      className={fieldClass('dataInicioCargo')}
                    />
                    {errors.dataInicioCargo && (
                      <p className={errorClass}>{errors.dataInicioCargo}</p>
                    )}
                  </div>

                  {/* E-mail */}
                  <div className="lg:col-span-1">
                    <label className={labelClass}>E-mail *</label>
                    <input
                      type="email"
                      required={!foundDirigente}
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        clearError('email');
                      }}
                      className={fieldClass('email')}
                      placeholder="email@exemplo.com"
                    />
                    {errors.email && (
                      <p className={errorClass}>{errors.email}</p>
                    )}
                  </div>

                  {/* Telefone */}
                  <div>
                    <label className={labelClass}>Telefone *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      value={formatPhone(formData.telefone)}
                      onChange={(e) => {
                        setFormData({ ...formData, telefone: e.target.value.replace(/\D/g, '') });
                        clearError('telefone');
                      }}
                      className={fieldClass('telefone')}
                      maxLength={15}
                      placeholder="(00) 00000-0000"
                    />
                    {errors.telefone && (
                      <p className={errorClass}>{errors.telefone}</p>
                    )}
                  </div>

                  {/* Nacionalidade */}
                  <div>
                    <label className={labelClass}>Nacionalidade *</label>
                    <input
                      type="text"
                      required
                      value={formData.nacionalidade}
                      onChange={(e) => setFormData({ ...formData, nacionalidade: toUpperText(e.target.value) })}
                      className={fieldBaseClass}
                    />
                  </div>

                  {/* CEP */}
                  <div className="sm:col-span-2 lg:col-span-1">
                    <label className={labelClass}>CEP *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        required={!foundDirigente}
                        value={formatCEP(formData.cep)}
                        onChange={(e) => {
                          setFormData({ ...formData, cep: e.target.value.replace(/\D/g, '') });
                          clearError('cep');
                        }}
                        className={fieldClass('cep', 'flex-1')}
                        maxLength={9}
                        placeholder="00000-000"
                      />
                      <button
                        type="button"
                        onClick={handleBuscarCep}
                        disabled={loadingCep}
                        className={`${primaryButtonClass} whitespace-nowrap gap-2`}
                      >
                        {loadingCep ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Buscando...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            Buscar CEP
                          </>
                        )}
                      </button>
                    </div>
                    {errors.cep && (
                      <p className={errorClass}>{errors.cep}</p>
                    )}
                  </div>

                  {/* Logradouro */}
                  <div className="sm:col-span-2 lg:col-span-2">
                    <label className={labelClass}>Logradouro *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      value={formData.logradouro}
                      onChange={(e) => {
                        setFormData({ ...formData, logradouro: toUpperText(e.target.value) });
                        clearError('logradouro');
                      }}
                      className={fieldClass('logradouro')}
                    />
                    {errors.logradouro && (
                      <p className={errorClass}>{errors.logradouro}</p>
                    )}
                  </div>

                  {/* Número */}
                  <div>
                    <label className={labelClass}>Número *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      maxLength={10}
                      value={formData.numero}
                      onChange={(e) => {
                        setFormData({ ...formData, numero: toUpperText(e.target.value) });
                        clearError('numero');
                      }}
                      className={fieldClass('numero')}
                    />
                    {errors.numero && (
                      <p className={errorClass}>{errors.numero}</p>
                    )}
                  </div>

                  {/* Complemento */}
                  <div className="lg:col-span-2">
                    <label className={labelClass}>Complemento</label>
                    <input
                      type="text"
                      maxLength={100}
                      placeholder="Ex: CHACARA 50B, Apto 12, Bloco A"
                      value={formData.complemento || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, complemento: toUpperText(e.target.value) });
                      }}
                      className={fieldBaseClass}
                    />
                  </div>

                  {/* Bairro */}
                  <div className="lg:col-span-2">
                    <label className={labelClass}>Bairro *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      value={formData.bairro}
                      onChange={(e) => {
                        setFormData({ ...formData, bairro: toUpperText(e.target.value) });
                        clearError('bairro');
                      }}
                      className={fieldClass('bairro')}
                    />
                    {errors.bairro && (
                      <p className={errorClass}>{errors.bairro}</p>
                    )}
                  </div>

                  {/* Cidade */}
                  <div className="lg:col-span-2">
                    <label className={labelClass}>Cidade *</label>
                    <input
                      type="text"
                      required={!foundDirigente}
                      value={formData.cidade}
                      onChange={(e) => {
                        setFormData({ ...formData, cidade: toUpperText(e.target.value) });
                        clearError('cidade');
                      }}
                      className={fieldClass('cidade')}
                    />
                    {errors.cidade && (
                      <p className={errorClass}>{errors.cidade}</p>
                    )}
                  </div>

                  {/* UF */}
                  <div>
                    <label className={labelClass}>UF *</label>
                    <select
                      required={!foundDirigente}
                      value={formData.uf}
                      onChange={(e) => {
                        setFormData({ ...formData, uf: e.target.value });
                        clearError('uf');
                      }}
                      className={fieldClass('uf')}
                    >
                      <option value="">Selecione</option>
                      {UF_OPTIONS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                    {errors.uf && (
                      <p className={errorClass}>{errors.uf}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {!foundDirigente && (
            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/70">
              <button
                type="button"
                onClick={() => navigate(`/dashboard/cadastro-dados-institucionais?id=${instituicaoId}`)}
                className={`${secondaryButtonClass} px-6`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`${primaryButtonClass} px-6`}
              >
                {loading ? 'Salvando...' : (isEditMode ? 'Atualizar' : 'Cadastrar')}
              </button>
            </div>
          )}
        </form>
      )}

      {/* Documentos tab */}
      {activeTab === 'documentos' && (
        <div>
          {isEditMode ? (
            <div className={`${panelClass} p-5`}>
              <DocumentosPessoaisUpload
                dirigenteId={dirigenteId!}
                cargo={formData.cargo}
                embedded
                onDocumentosChange={() => { /* optional: reload or show toast */ }}
              />
            </div>
          ) : (
            <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800 shadow-sm dark:border-orange-500/40 dark:bg-orange-950/20 dark:text-orange-200">
              Salve o dirigente antes de gerenciar documentos pessoais.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CadastroDirigentePage;

